import React, { useState, useEffect, useMemo } from "react";
import api from "@/api";
import SecuredImage from "./SecuredImage";
import { updateUserSettings } from "../settings";
import DataSearchFilters from "./search/DataSearchFilters";
import useDataSearch from "../hooks/useDataSearch";
import MasterDataRowActions, { ArchivedBadge, ShowArchivedToggle } from "./masterdata/ArchiveControls";
import { visibleRows } from "@/utils/archive";

// The API sends roles as a list (Spatie role objects, or plain names)
const roleNames = (u) => (Array.isArray(u.roles) ? u.roles : []).map((r) => r?.name ?? r).filter(Boolean);

// Searchable columns, as shown in the table
const SEARCH_FIELDS = [
  { key: "name", label: "Name", type: "text", value: (u) => u.name },
  { key: "email", label: "Email", type: "text", value: (u) => u.email },
  { key: "initials", label: "Initials", type: "text", value: (u) => u.initials },
  { key: "role", label: "Role", type: "select", value: roleNames },
];

export default function UserTable({
  onCreateClick,
  onUsersLoaded,
  onEditClick, // NEW: Add this prop
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const stored = sessionStorage.getItem("usersPerPage");
    if (stored === "all") return "all";
    const num = stored ? Number(stored) : 15;
    return [15, 25, 50].includes(num) ? num : 15;
  });

  const [showArchived, setShowArchived] = useState(false);

  function fetchUsers() {
    setLoading(true);
    api
      .post("/masterdata/users", {})
      .then((res) => {
        // Sort users alphabetically by name
        const sortedUsers = res.data.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setUsers(sortedUsers);
        if (onUsersLoaded) onUsersLoaded(sortedUsers);
      })
      .catch((err) => setError(err.message || "Failed to load users"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  // Persist page size
  useEffect(() => {
    sessionStorage.setItem("usersPerPage", String(pageSize));
  }, [pageSize]);

  // Filter users by search (already sorted)
  const shown = useMemo(() => visibleRows(users, showArchived), [users, showArchived]);
  const search = useDataSearch(shown, SEARCH_FIELDS, { onChange: () => setPage(1) });
  const filtered = search.filtered;

  // Pagination
  const totalPages =
    pageSize === "all" ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = useMemo(() => {
    if (pageSize === "all") return filtered;
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // Ensure page within bounds
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  return (
    <div className="w-full">
      <DataSearchFilters {...search.panelProps} />
      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <ShowArchivedToggle checked={showArchived} onChange={setShowArchived} />
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 px-3 py-2 rounded flex items-center gap-1 border border-gray-300 dark:border-gray-600"
            onClick={() => {
              setLoading(true);
              api
                .post("/masterdata/users", {})
                .then((res) => {
                  // Sort users alphabetically by name on refresh too
                  const sortedUsers = res.data.sort((a, b) => {
                    const nameA = (a.name || "").toLowerCase();
                    const nameB = (b.name || "").toLowerCase();
                    return nameA.localeCompare(nameB);
                  });
                  setUsers(sortedUsers);
                  if (onUsersLoaded) onUsersLoaded(sortedUsers);
                })
                .catch((err) => setError(err.message || "Failed to load users"))
                .finally(() => setLoading(false));
            }}
            title="Refresh"
            disabled={loading}
          >
            <svg
              className={loading ? "animate-spin w-4 h-4" : "w-4 h-4"}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582M20 20v-5h-.581M5.582 9A7.974 7.974 0 0112 4c2.21 0 4.21.896 5.657 2.343M18.418 15A7.974 7.974 0 0112 20a7.974 7.974 0 01-5.657-2.343"
              />
            </svg>
            <span className="hidden sm:inline text-sm font-medium">
              Refresh
            </span>
          </button>
          <button
            className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow"
            onClick={onCreateClick}
          >
            + Add User
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-gray-400 dark:text-gray-500 py-10 text-sm">
          Loading users...
        </div>
      ) : error ? (
        <div className="text-center text-red-600 dark:text-red-400 py-10 text-sm">
          {error}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow">
            <table className="min-w-full align-middle text-sm whitespace-nowrap border-separate border-spacing-0">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <th className="bg-gray-100/75 px-3 py-3 text-center font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                    Photo
                  </th>
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                    Name
                  </th>
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                    Email
                  </th>
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                    Initials
                  </th>
                  <th className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                    Role
                  </th>
                  <th className="bg-gray-100/75 px-3 py-3 text-right font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="p-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      No users found.
                    </td>
                  </tr>
                ) : (
                  paginated.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="px-3 py-2 text-center">
                        {user.avatar ? (
                          <SecuredImage
                            src={
                              user.avatar.startsWith("http")
                                ? user.avatar
                                : `avatars/${user.avatar}`
                            }
                            alt={user.name}
                            className="inline-block size-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="inline-block size-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-300 font-bold">
                            {user.initials || "?"}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium">{user.name}<ArchivedBadge row={user} /></p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">
                          {user.title || ""}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {user.email}
                      </td>
                      <td className="px-3 py-2">{user.initials || "-"}</td>
                      <td className="px-3 py-2">{roleNames(user).join(", ") || "-"}</td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm leading-5 font-semibold text-gray-800 hover:border-gray-300 hover:text-gray-900 hover:shadow-xs focus:ring-3 focus:ring-gray-300/25 active:border-gray-200 active:shadow-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-gray-200 dark:focus:ring-gray-600/40 dark:active:border-gray-700"
                            onClick={() => onEditClick && onEditClick(user)} // NEW: Call onEditClick with user data
                          >
                            Edit
                          </button>
                          <MasterDataRowActions type="users" row={user} name={user.name || user.email} noun="user" onChanged={fetchUsers} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-4 w-full">
            {/* Always shown, also with one page: how many users the search finds, and which page this is */}
            <span className="text-sm font-medium" data-testid="users-count">
              {filtered.length} {filtered.length === 1 ? "user" : "users"}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 rounded border bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="px-2 text-sm font-semibold">
                Page {page} of {totalPages}
              </span>
              <button
                className="px-3 py-1 rounded border bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
            <label className="inline-flex items-center gap-2 text-sm font-medium ml-auto">
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "all") {
                    setPageSize("all");
                    setPage(1);
                    setLoading(true);
                    api
                      .post("/masterdata/users", {})
                      .then((res) => {
                        // Sort users alphabetically by name
                        const sortedUsers = res.data.sort((a, b) => {
                          const nameA = (a.name || "").toLowerCase();
                          const nameB = (b.name || "").toLowerCase();
                          return nameA.localeCompare(nameB);
                        });
                        setUsers(sortedUsers);
                        if (onUsersLoaded) onUsersLoaded(sortedUsers);
                      })
                      .catch((err) =>
                        setError(err.message || "Failed to load users")
                      )
                      .finally(() => setLoading(false));
                    updateUserSettings({ users_per_page: "all" });
                  } else {
                    const num = Number(val);
                    setPageSize(num);
                    setPage(1);
                    setLoading(true);
                    api
                      .post("/masterdata/users", {})
                      .then((res) => {
                        // Sort users alphabetically by name
                        const sortedUsers = res.data.sort((a, b) => {
                          const nameA = (a.name || "").toLowerCase();
                          const nameB = (b.name || "").toLowerCase();
                          return nameA.localeCompare(nameB);
                        });
                        setUsers(sortedUsers);
                        if (onUsersLoaded) onUsersLoaded(sortedUsers);
                      })
                      .catch((err) =>
                        setError(err.message || "Failed to load users")
                      )
                      .finally(() => setLoading(false));
                    updateUserSettings({ users_per_page: num });
                  }
                }}
                className="form-select rounded border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
              >
                {[15, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
                <option value="all">All</option>
              </select>
            </label>
          </div>
        </>
      )}
    </div>
  );
}
